import { Injectable } from "@angular/core";
import { datadogRum, RumEvent } from "@datadog/browser-rum";
import { environment } from "src/environments/environment";

import {
  DATADOG_EXCLUDED_ACTIVITY_URLS,
  isInflatedActionEvent,
} from "../utils/datadog-activity-exclusions.util";
import { createDuplicateErrorThrottle } from "../utils/datadog-error-throttle.util";
import { isPendoRumNoise } from "../utils/pendo-rum-noise.util";
import { isResizeObserverNoise } from "../utils/resize-observer-noise.util";
import { isThirdPartyRumNoise } from "../utils/third-party-rum-noise.util";

@Injectable({ providedIn: "root" })
export class DataDogService {
  // Stateful; this root service gives one instance per app load.
  private readonly isThrottledDuplicateError = createDuplicateErrorThrottle();

  initDataDog(): void {
    if (environment.DATA_DOG_ID) {
      datadogRum.init({
        applicationId: "258b973a-bbd1-4ad9-98f8-b630d5798747",
        clientToken: environment.DATA_DOG_ID,
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: "datadoghq.com",
        service: "lmn-swa-pro-web-customer-portal",
        env: environment.DATA_DOG_ENV,
        // Specify a version number to identify the deployed version of your application in Datadog
        version: environment.DATA_DOG_VERSION,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        trackBfcacheViews: true,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        defaultPrivacyLevel: "mask-user-input",
        trackViewsManually: false,
        allowedTracingUrls: [
          /http:\/\/localhost:.*/,
          /http:\/\/.*\.golmn\.com/,
          /https:\/\/.*\.golmn\.com/,
          /https:\/\/.*groundsguyspro\.com/,
        ],
        beforeSend: (event) => this.shouldSendRumEvent(event),
        excludedActivityUrls: DATADOG_EXCLUDED_ACTIVITY_URLS,
      });
    }
  }

  // The throttle runs last so dropped noise doesn't use up its budget.
  private shouldSendRumEvent(event: RumEvent): boolean {
    return (
      !isResizeObserverNoise(event) &&
      !isInflatedActionEvent(event) &&
      !isPendoRumNoise(event) &&
      !isThirdPartyRumNoise(event) &&
      !this.isThrottledDuplicateError(event)
    );
  }
}
